/**
 * `buildCardFileEmbedExtension` — the attachment-backed inline `fileEmbed`
 * node for the card-description + comment editor surfaces
 * (`surface === 'card'` / `'comment'`). Mirrors the wiki
 * `buildFileEmbedExtension` visually but is deliberately decoupled from the
 * wiki `WikiEmbedOrchestrator` (no lightbox / no window-event coupling —
 * cards + comments don't mount that orchestrator; the chip downloads
 * directly on click).
 *
 * Why a durable node at all? Non-image files embed INLINE in the comment
 * body as a chip (same shape as the inline image embed). Storing the
 * `attachmentId` lets the NodeView resolve a fresh signed GET URL on click
 * (5-min TTL), so a chip authored minutes ago still downloads.
 *
 * Schema shape — inline atomic (mirrors the wiki file embed):
 *   - `group: 'inline'` + `inline: true` + `atom: true`
 *   - `selectable: true`, `draggable: false`, `marks: ''`
 *
 * Attribute shape:
 *   - `attachmentId` — UUID of the `attachments` row (null while the
 *     upload is in flight). Rides through TipTap JSON losslessly (the BE
 *     `full`-tier validator whitelists the node name and allows extra
 *     attrs). The BE text / preview walkers read `fileName`.
 *   - `fileName` — original filename shown in the chip.
 *   - `sizeBytes` — file size for the chip's size column.
 *   - `mimeType` — drives file-type icon selection.
 *   - `uploadingPlaceholderId` (transient) — a client-side id the drop
 *     hook uses to locate this node once the upload settles (to swap in
 *     the resolved `attachmentId`, or to delete the node on failure).
 *     Never round-trips through HTML / markdown.
 *   - `uploadingFailed` (transient) — set by the comment composers when the
 *     upload failed: the chip stays in the body as an error state (Retry /
 *     Dismiss) and the composer re-targets it by `uploadingPlaceholderId`
 *     on Retry. Never round-trips.
 */

import { Node, type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

export interface BuildCardFileEmbedExtensionOptions {
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
}

export function buildCardFileEmbedExtension({ view }: BuildCardFileEmbedExtensionOptions) {
  return Node.create({
    name: 'fileEmbed',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,
    marks: '',

    addAttributes() {
      return {
        attachmentId: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-attachment-id')
            return isUuid(raw) ? raw : null
          },
          renderHTML: (attrs: { attachmentId: string | null }) =>
            attrs.attachmentId ? { 'data-attachment-id': attrs.attachmentId } : {},
        },
        fileName: {
          default: '',
          parseHTML: (el: HTMLElement) => el.getAttribute('data-file-name') ?? '',
          renderHTML: (attrs: { fileName: string }) =>
            attrs.fileName ? { 'data-file-name': attrs.fileName } : {},
        },
        sizeBytes: {
          default: null as number | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-size-bytes')
            if (raw === null) return null
            const n = Number.parseInt(raw, 10)
            return Number.isFinite(n) && n >= 0 ? n : null
          },
          renderHTML: (attrs: { sizeBytes: number | null }) =>
            attrs.sizeBytes !== null && attrs.sizeBytes >= 0
              ? { 'data-size-bytes': String(attrs.sizeBytes) }
              : {},
        },
        mimeType: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => el.getAttribute('data-mime-type'),
          renderHTML: (attrs: { mimeType: string | null }) =>
            attrs.mimeType ? { 'data-mime-type': attrs.mimeType } : {},
        },
        uploadingPlaceholderId: {
          // In-memory-only — used by the drop hook to locate the node
          // while the upload is in flight.
          default: null as string | null,
          parseHTML: () => null,
          renderHTML: () => ({}),
        },
        uploadingFailed: {
          // In-memory-only — flips the uploading chip into its error
          // state (Retry / Dismiss). Posting is gated while set.
          default: false,
          parseHTML: () => false,
          renderHTML: () => ({}),
        },
      }
    },

    parseHTML() {
      return [{ tag: 'span[data-block-type="card-file-embed"]' }]
    },

    renderHTML({ HTMLAttributes }) {
      return ['span', { ...HTMLAttributes, 'data-block-type': 'card-file-embed' }]
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },
  })
}

export { isUuid as isCardFileAttachmentId }
